import {
    createRouter,
    createWebHistory,
    RouteLocationNormalized,
    RouteRecordRaw
} from 'vue-router';
import NProgress from 'nprogress';
import { onPage } from '@/composables/useHistory';
import { AppName, BaseUrl } from '@/composables/useConfig';
import { getExtensionFromString } from '@/utils/helper';

import guestRoutes from './guest';
import authRoutes from './auth';
import { fetchUser, userState } from '@/composables/useUser';
import { fetchOrganization, organizationState } from '@/composables/useOrganization';

declare module 'vue-router' {
    interface RouteMeta {
        guest?: boolean;
        auth?: boolean;
    }
}

const routes: Array<RouteRecordRaw> = [
    ...authRoutes,
    ...guestRoutes,
    {
        path: '/:pathMatch(.*)*',
        name: 'catchAll',
        redirect: { name: 'notFound' }
    }
];

const updateDocumentTitle = (to: RouteLocationNormalized) => {
    document.title = `${to.meta.title ? `${to.meta.title} | ` : ''}${
        AppName as string
    }`;
};

const updateDocumentIcon = (to: RouteLocationNormalized) => {
    const faviconElements = document.querySelectorAll('link[rel="icon"]');
    faviconElements.forEach((el) => {
        el.setAttribute(
            'href',
            `${BaseUrl}${
                to.meta.icon ? (to.meta.icon as string) : 'favicon'
            }${getExtensionFromString(el.getAttribute('href') ?? '')}`
        );
    });
};

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
});

router.beforeResolve((to, from, next) => {
    if (to.path) {
        NProgress.start();
        onPage(from, to);
    }
    next();
});

router.afterEach(() => {
    NProgress.done();
});

router.beforeEach(async (to, from, next) => {
    updateDocumentTitle(to);
    updateDocumentIcon(to);

    if (!organizationState.check()) {
        await fetchOrganization();
    }

    if (to.meta.guest) {
        if (!['/', '/maintenance', '/404'].includes(to.path) && userState.check()) {
            return next({ name: 'dashboard' });
        }
    } else if (!userState.check()) {
        await fetchUser();
    }

    if (to.meta.auth) {
        if (!userState.check()) {
            return next({ name: 'login' });
        }
    }

    return next();
});

// Vue Lazy Routes & loading chunk failed
// https://blog.francium.tech/vue-lazy-routes-loading-chunk-failed-9ee407bbd58
router.onError((error) => {
    if ((/loading chunk \d* failed./i).test(error.message)) {
        window.location.reload();
    }
});

export default router;
